import React, { useId, useMemo } from 'react';
import type { CipLoop } from '../../types/prodCipLoop';
import type { ResourceNode } from '../../types/prodResourceTopology';
import './CipLoopRelationshipSvg.css';

interface Props {
  loop: CipLoop | null;
  resources: ResourceNode[];
  focusStation?: ResourceNode | null;
}

const shortLabel = (value: string, maxLength = 24) => value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value;
const label = (resource: ResourceNode | null | undefined, fallback: string) => resource ? `${resource.name} · ${resource.code}` : fallback;

function RelationshipArrow({ markerId, className, d }: { markerId: string; className: string; d: string }) {
  return <path className={className} d={d} markerEnd={`url(#${markerId})`} />;
}

export default function CipLoopRelationshipSvg({ loop, resources, focusStation = null }: Props) {
  const byId = useMemo(() => new Map(resources.map((resource) => [resource.id, resource])), [resources]);
  const markerId = `cip-loop-arrow-${useId().replace(/:/g, '')}`;
  const primary = loop ? byId.get(loop.primaryStationResourceId ?? '') : focusStation;
  const memberResources = loop?.memberResourceIds.map((id) => byId.get(id)).filter((resource): resource is ResourceNode => Boolean(resource)) ?? [];
  const memberPreview = memberResources.slice(0, 2).map((resource) => resource.name).join(' · ');
  const memberCount = loop?.memberResourceIds.length ?? 0;
  const backupCount = loop?.backupStationResourceIds.length ?? 0;
  const stationLabel = label(primary, loop ? '尚未选择主 CIP 站' : '选择一个 CIP 服务站');
  const loopLabel = loop ? loop.name : '创建逻辑回路';
  const loopSubLabel = loop ? loop.code : '定义服务范围，不进入物理资源树';
  const memberSummary = memberCount ? `${memberCount} 个可清洗成员` : loop ? '尚未配置成员' : '加入设备与组件';
  const memberDetail = memberPreview || '成员明细在下方配置区维护';

  return (
    <svg className={`cip-loop-relationship-svg${loop ? '' : ' cip-loop-relationship-svg-overview'}`} viewBox="0 0 920 164" role="img" aria-label={loop ? `${loop.name} 的 CIP 服务关系摘要` : 'CIP 逻辑回路的服务关系摘要'}>
      <defs>
        <marker id={markerId} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
          <path d="M 0 0 L 8 4 L 0 8 z" className="cip-loop-svg-arrow" />
        </marker>
      </defs>
      <RelationshipArrow markerId={markerId} className="cip-loop-svg-primary-line" d="M 248 74 C 288 74, 312 74, 352 74" />
      <RelationshipArrow markerId={markerId} className="cip-loop-svg-member-line" d="M 568 74 C 608 74, 632 74, 672 74" />
      <text className="cip-loop-svg-line-label" x="282" y="54">正常服务</text>
      <text className="cip-loop-svg-line-label" x="596" y="54">清洗覆盖</text>

      <g className="cip-loop-svg-card cip-loop-svg-station">
        <rect x="28" y="30" width="220" height="88" rx="8" />
        <text x="48" y="56" className="cip-loop-svg-kicker">主 CIP 站</text>
        <text x="48" y="82" className="cip-loop-svg-title">{shortLabel(stationLabel)}</text>
        <text x="48" y="104" className="cip-loop-svg-subtitle">正常排产的清洗服务主体</text>
      </g>

      <g className="cip-loop-svg-card cip-loop-svg-loop">
        <rect x="352" y="30" width="216" height="88" rx="8" />
        <text x="372" y="56" className="cip-loop-svg-kicker">逻辑 CIP 回路</text>
        <text x="372" y="82" className="cip-loop-svg-title">{shortLabel(loopLabel)}</text>
        <text x="372" y="104" className="cip-loop-svg-subtitle">{shortLabel(loopSubLabel, 30)}</text>
      </g>

      <g className="cip-loop-svg-card cip-loop-svg-members">
        <rect x="672" y="30" width="220" height="88" rx="8" />
        <text x="692" y="56" className="cip-loop-svg-kicker">回路成员</text>
        <text x="692" y="82" className="cip-loop-svg-title">{memberSummary}</text>
        <text x="692" y="104" className="cip-loop-svg-subtitle">{shortLabel(memberDetail, 30)}</text>
      </g>

      <g className="cip-loop-svg-summary">
        <rect x="352" y="132" width="216" height="20" rx="4" />
        <text x="460" y="146" textAnchor="middle" className="cip-loop-svg-summary-label">应急备站：{backupCount} 个 · 仅人工指定</text>
      </g>
      <text className="cip-loop-svg-footnote" x="28" y="146">关系图固定为摘要；成员与备站明细在下方配置区维护。</text>
    </svg>
  );
}
